import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { ApiError } from '@/api/envelope';
import type { PageCreate, PagePreview } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { testIdAttr } from '@/lib/testIds';

import { refusalOf } from './pageRefusals';

/** What the drawer says when the server could not render the page (P38). */
const PREVIEW_FAILED = 'The preview could not be shown.';

type Shown =
  | { kind: 'waiting' }
  | { kind: 'ready'; page: PagePreview }
  | { kind: 'refused'; sentence: string }
  | { kind: 'failed' }
  | { kind: 'silent' };

interface Answer {
  body: PageCreate;
  attempt: number;
  shown: Shown;
}

interface PreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The B1 body to preview; null while a card's page is still being read. */
  body: PageCreate | null;
  /** A card's read of the saved page, when the drawer was opened from one. */
  read?: { failed: boolean; retry: () => void };
  /** Asks the server to render the body (B10); the screens pass the hook's. */
  request: (body: PageCreate) => Promise<PagePreview>;
  /** A refusal naming a field, for the editor to put on that field too. */
  onRefused?: (error: ApiError) => void;
}

/**
 * The page as the app would show it, rendered by the backend from the
 * unsaved body (P16, PB11, D16): header image, title, summary, body and
 * linked items, at phone width. Nothing is saved.
 *
 * The body's HTML is shown in a frame with an empty `sandbox`, so nothing in
 * it runs or reaches the portal's document. That gives the frame an opaque
 * origin whose height cannot be read, so it has a fixed height and scrolls
 * inside (proposed P44).
 */
export function PreviewDrawer({
  open,
  onClose,
  body,
  read,
  request,
  onRefused,
}: PreviewDrawerProps) {
  const headingId = useId();
  const [attempt, setAttempt] = useState(0);
  // The answer, with the body and attempt it answers: any other is waiting.
  const [answer, setAnswer] = useState<Answer | null>(null);
  const shown: Shown =
    answer && answer.body === body && answer.attempt === attempt
      ? answer.shown
      : { kind: 'waiting' };
  // Only the newest request's answer is kept.
  const asked = useRef(0);

  useEffect(() => {
    if (!open || !body) return;
    const ask = ++asked.current;
    const settle = (next: Shown) => {
      if (ask === asked.current) setAnswer({ body, attempt, shown: next });
    };
    request(body).then(
      (page) => settle({ kind: 'ready', page }),
      (error: unknown) => {
        const { kind, sentence } = refusalOf(error);
        // The session's and the organization context's: handled outside (PNF5).
        if (kind === 'outside') settle({ kind: 'silent' });
        else if (kind === 'failed') settle({ kind: 'failed' });
        else {
          settle({ kind: 'refused', sentence });
          if (kind === 'invalid' && ask === asked.current) onRefused?.(error as ApiError);
        }
      },
    );
    // `request` and `onRefused` are the screens' stable functions; a new body
    // or a Retry asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, body, attempt]);

  const focusOnArrival = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus();
  }, []);

  function close() {
    asked.current += 1;
    setAnswer(null);
    onClose();
  }

  let content;
  if (read?.failed) {
    content = (
      <ErrorState
        title="The page could not be read."
        onRetry={read.retry}
        testId="page-preview-drawer-read"
      />
    );
  } else if (!body || shown.kind === 'waiting') {
    content = (
      <Box data-testid="page-preview-drawer-loading">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Loading the preview
        </Typography>
        <LinearProgress aria-label="Loading the preview" />
      </Box>
    );
  } else if (shown.kind === 'refused' || shown.kind === 'failed') {
    content = (
      <Alert
        severity="error"
        data-testid="page-preview-drawer-error"
        action={
          shown.kind === 'failed' ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAttempt((count) => count + 1)}
              data-testid="page-preview-drawer-retry-button"
            >
              Retry
            </Button>
          ) : undefined
        }
      >
        {shown.kind === 'refused' ? shown.sentence : PREVIEW_FAILED}
      </Alert>
    );
  } else if (shown.kind === 'ready') {
    const { page } = shown;
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {page.header_image?.url ? (
          <Box
            component="img"
            src={page.header_image.url}
            alt={page.header_image.alt ?? ''}
            data-testid="page-preview-drawer-image"
            sx={{ width: '100%', borderRadius: 1, display: 'block' }}
          />
        ) : null}
        <Typography variant="h5" component="h3" data-testid="page-preview-drawer-title">
          {page.title}
        </Typography>
        {page.summary ? (
          <Typography
            variant="body1"
            color="text.secondary"
            data-testid="page-preview-drawer-summary-value"
          >
            {page.summary}
          </Typography>
        ) : null}
        <Box
          component="iframe"
          sandbox=""
          srcDoc={page.html ?? ''}
          title="Page content"
          data-testid="page-preview-drawer-frame"
          sx={{
            width: '100%',
            height: '60vh',
            minHeight: 320,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        />
        {page.links.length > 0 ? (
          <Box
            component="ul"
            aria-label="Linked items"
            data-testid="page-preview-drawer-links-list"
            sx={{ m: 0, pl: 3 }}
          >
            {page.links.map((link) => (
              <li key={`${link.resource_type}-${link.resource_id}`}>
                <Typography variant="body2">{link.title}</Typography>
              </li>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  } else {
    content = null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      // Above the top bar, which sits one layer over a drawer: otherwise it
      // hides the heading and the Close button.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      // The heading takes focus instead of the drawer's own container; focus
      // is still held inside while open, and goes back to the opener on close.
      ModalProps={{ disableAutoFocus: true }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': headingId,
          ...testIdAttr('page-preview-drawer'),
          sx: { width: { xs: '100%', sm: 360 }, p: 3 },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Typography
          id={headingId}
          ref={focusOnArrival}
          tabIndex={-1}
          variant="h6"
          component="h2"
          sx={{ flex: 1, '&:focus': { outline: 'none' } }}
        >
          Preview
        </Typography>
        <IconButton
          aria-label="Close preview"
          onClick={close}
          edge="end"
          data-testid="page-preview-drawer-close-button"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        data-testid="page-preview-drawer-note-caption"
        sx={{ mb: 2 }}
      >
        Preview, app styling may differ
      </Typography>
      {content}
    </Drawer>
  );
}
